/**
 * The command palette's client-side matcher.
 *
 * Only the *static* command list is filtered here — remote hits are already
 * filtered by the server and are shown as returned, so re-scoring them locally
 * would hide rows the backend deliberately matched (a bulletin found by its
 * body text has nothing in its title to match against).
 *
 * Subsequence matching, not substring: typing "nf" should find "New file" the
 * way an editor's command palette does. Scoring then decides ordering, because
 * a subsequence match on its own is far too permissive to rank by — "nf"
 * technically matches "Notify officer of filing" too, and the exact prefix has
 * to win.
 */

/** Fold case and strip diacritics so "reglement" matches "Règlement" (FR parity). */
export function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

const SCORE_EXACT = 1000;
const SCORE_PREFIX = 500;
const SCORE_WORD_PREFIX = 250;
const SCORE_SUBSTRING = 100;

/** Bonus per character that starts a word — favours acronym-style typing. */
const BONUS_WORD_START = 12;
/** Bonus per character matched immediately after the previous one. */
const BONUS_CONSECUTIVE = 8;
/** Penalty per character skipped, so tight matches outrank scattered ones. */
const PENALTY_GAP = 1;

function isWordBoundary(haystack: string, index: number): boolean {
  if (index === 0) return true;
  return /[\s\-_/.,:]/.test(haystack[index - 1]);
}

/**
 * Score `query` against `text`, or return `null` when it doesn't match at all.
 * Higher is better; scores are only comparable within a single query.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const q = foldForSearch(query.trim());
  const t = foldForSearch(text);
  if (q === '') return 0;
  if (t === '') return null;

  if (t === q) return SCORE_EXACT;
  if (t.startsWith(q)) return SCORE_PREFIX;

  const substringAt = t.indexOf(q);
  if (substringAt >= 0) {
    // A hit at a word boundary ("file" in "New file") reads as intentional;
    // one mid-word ("ile" in "file") is a coincidence. Rank them apart.
    return (isWordBoundary(t, substringAt) ? SCORE_WORD_PREFIX : SCORE_SUBSTRING) - substringAt;
  }

  // Fall back to a scattered subsequence walk.
  let score = 0;
  let cursor = 0;
  let previousIndex = -1;
  for (const char of q) {
    const found = t.indexOf(char, cursor);
    if (found === -1) return null;
    if (isWordBoundary(t, found)) score += BONUS_WORD_START;
    if (found === previousIndex + 1) score += BONUS_CONSECUTIVE;
    else score -= Math.min(found - cursor, 10) * PENALTY_GAP;
    previousIndex = found;
    cursor = found + 1;
  }
  return score;
}

/** An item the palette can rank locally. */
export interface FuzzyCandidate {
  title: string;
  subtitle?: string;
  /** Extra terms to match on that aren't shown (synonyms, EN term on an FR label). */
  keywords?: string;
}

/**
 * Best score across an item's searchable text. The title dominates: a query
 * that matches a subtitle or a hidden keyword still matches, but never
 * outranks an item whose visible label matched the same query.
 */
export function scoreCandidate(query: string, item: FuzzyCandidate): number | null {
  const title = fuzzyScore(query, item.title);
  const subtitle = item.subtitle ? fuzzyScore(query, item.subtitle) : null;
  const keywords = item.keywords ? fuzzyScore(query, item.keywords) : null;

  const secondary = Math.max(subtitle ?? -Infinity, keywords ?? -Infinity);
  if (title === null && secondary === -Infinity) return null;
  // Halved so a subtitle hit can order items among themselves but can't leapfrog
  // a title hit of the same query.
  return Math.max(title ?? -Infinity, secondary / 2);
}

/**
 * Filter and rank `items`, keeping the original order among equal scores so an
 * empty query renders the caller's curated command order untouched.
 */
export function fuzzyFilter<T extends FuzzyCandidate>(query: string, items: readonly T[]): T[] {
  if (query.trim() === '') return [...items];
  return items
    .map((item, index) => ({ item, index, score: scoreCandidate(query, item) }))
    .filter((row): row is { item: T; index: number; score: number } => row.score !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((row) => row.item);
}
